import * as React from "react";
import { css, SerializedStyles } from "@emotion/react";
import f from "@/module/Utils/emotion";
import v from "@/module/Utils/variable";

// Images
import Arrow from "@/images/common/icon_arrow";
import Replay from "@/images/common/icon_replay";
import Rect from "@/images/common/icon_rect";
import IconCross from "@/images/common/icon_cross";
import IconWindow from "@/images/common/icon_window";

interface ButtonProps {
  BgColor?: "white" | "black" | "alpha";
  iconColor?: "red" | "white";
  radius?: number;
  icon?: "replay" | "arrow" | "rect" | "cross" | "window" | "none";
  hover?: boolean;
}

const DefaultButton: React.FC<ButtonProps> = (props) => {
  const getBgColor = () => {
    switch (props.BgColor) {
      case "white":
        return { before: "#fff", after: "#222222" };
      case "black":
        return { before: "#222222", after: "#fff" };
      case "alpha":
        return { before: "rgba(0,0,0,0)", after: "#fff" };
    }
  };

  const getIconColor = () => {
    switch (props.iconColor) {
      case "red":
        return { before: v.red, after: "#fff" };
      case "white":
        return { before: "#fff", after: v.red };
    }
  };

  const iconColor = getIconColor();
  const bgColor = getBgColor();

  // Style
  const iconPseudoStyle = css({
    "&:before, &:after": {
      transition: "opacity 0.5s ease-out",
      zIndex: 3,
    },
    [f.pc()]: {
      "&:hover": props.hover
        ? {
            "&:before": {
              opacity: 0,
            },
            "&:after": {
              opacity: 1,
            },
          }
        : {},
    },
  });

  const iconStyle = () => {
    if (props.icon === "replay") {
      return css(iconPseudoStyle, {
        "&:before, &:after": {
          [f.sp()]: {
            right: f.vw(46, v.spWidth),
            width: f.vw(24, v.spWidth),
            height: f.vw(27.8, v.spWidth),
          },
          [f.pc()]: {
            right: f.vw(30),
            width: f.vw(15.45),
            height: f.vw(17.9),
          },
        },
        ":before": {
          backgroundImage: `url(${Replay(iconColor.before)})`,
        },
        "&:after": {
          backgroundImage: `url(${Replay(iconColor.after)})`,
          opacity: 0,
        },
      });
    } else if (props.icon === "arrow") {
      return css(iconPseudoStyle, {
        "&:before, &:after": {
          [f.sp()]: {
            right: f.vw(46, v.spWidth),
            width: f.vw(21.21, v.spWidth),
            height: f.vw(20.74, v.spWidth),
          },
          [f.pc()]: {
            right: f.vw(26),
            width: f.vw(12.01),
            height: f.vw(11.74),
          },
        },
        ":before": {
          backgroundImage: `url(${Arrow(iconColor.before)})`,
        },
        "&:after": {
          backgroundImage: `url(${Arrow(iconColor.after)})`,
          opacity: 0,
        },
      });
    } else if (props.icon === "rect") {
      return css(iconPseudoStyle, {
        "&:before, &:after": {
          [f.sp()]: {
            right: f.vw(45, v.spWidth),
            width: f.vw(26, v.spWidth),
            height: f.vw(26, v.spWidth),
          },
          [f.pc()]: {
            right: f.vw(24),
            width: f.vw(17),
            height: f.vw(17),
          },
        },
        ":before": {
          backgroundImage: `url(${Rect(iconColor.before)})`,
        },
        "&:after": {
          backgroundImage: `url(${Rect(iconColor.after)})`,
          opacity: 0,
        },
      });
    } else if (props.icon === "cross") {
      return css(iconPseudoStyle, {
        "&:before, &:after": {
          [f.sp()]: {
            right: f.vw(45, v.spWidth),
            width: f.vw(26, v.spWidth),
            height: f.vw(26, v.spWidth),
          },
          [f.pc()]: {
            right: f.vw(24),
            width: f.vw(17),
            height: f.vw(17),
          },
        },
        ":before": {
          backgroundImage: `url(${IconCross(iconColor.before)})`,
        },
        "&:after": {
          backgroundImage: `url(${IconCross(iconColor.after)})`,
          opacity: 0,
        },
      });
    } else if (props.icon === "window") {
      return css(iconPseudoStyle, {
        "&:before, &:after": {
          [f.sp()]: {
            right: f.vw(31.4, v.spWidth),
            width: f.vw(21.27, v.spWidth),
            height: f.vw(20.91, v.spWidth),
          },
          [f.pc()]: {
            right: f.vw(24.6),
            width: f.vw(15.79),
            height: f.vw(15.52),
          },
        },
        ":before": {
          backgroundImage: `url(${IconWindow(iconColor.before)})`,
        },
        "&:after": {
          backgroundImage: `url(${IconWindow(iconColor.after)})`,
          opacity: 0,
        },
      });
    } else {
      return null;
    }
  };

  const buttonStyle = css(iconStyle(), {
    position: "relative",
    width: "100%",
    height: "100%",
    fontSize: "inherit",
    letterSpacing: "inherit",
    lineHeight: "inherit",
    backgroundColor: bgColor.before,
    color: iconColor.before,
    fill: iconColor.before,
    border: `solid 1px ${props.BgColor === "alpha" ? "#fff" : bgColor.before}`,
    [f.sp()]: {
      borderRadius: f.vw(props.radius, v.spWidth),
    },
    [f.pc()]: {
      transition:
        "background-color 0.5s ease-out, border 0.5s ease-out, color 0.5s ease-out, fill 0.5s ease-out",
      borderRadius: f.vw(50),
    },
    "&:after, &:before": {
      content: "''",
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      backgroundSize: "99% 99%",
      backgroundPosition: "top left",
      backgroundRepeat: "no-repeat",
      pointerEvents: "none",
    },
    "a, button": {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      height: "100%",
      fontSize: "inherit",
      letterSpacing: "inherit",
      lineHeight: "inherit",
    },

    "&:hover": props.hover
      ? {
          [f.pc()]: {
            backgroundColor: bgColor.after,
            border: `solid 1px ${bgColor.after}`,
            color: iconColor.after,
            fill: iconColor.after,
          },
        }
      : {},
  });

  return <div css={buttonStyle}>{props.children}</div>;
};

DefaultButton.defaultProps = {
  BgColor: "white",
  radius: 50,
  icon: "none",
  iconColor: "white",
  hover: true,
};

export default DefaultButton;
